import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Eye } from 'lucide-react';
import api from '../../utils/api';

const BlogManagement = () => {
  const [blogs, setBlogs] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      const response = await api.get('/blogs');
      if (response.data.success) {
        setBlogs(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching blogs:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this blog post?')) return;
    try {
      await api.delete(`/blogs/admin/${id}`);
      fetchBlogs();
    } catch (error) {
      alert('Error deleting blog');
    }
  };

  return (
    <div className="p-8">
      <div className="flex justify-between items-start mb-8">
        <div>
          <h1 className="text-5xl font-black mb-2">BLOG & CONTENT</h1>
          <div className="inline-block bg-yellow-400 px-4 py-1 font-mono font-bold text-xs border-2 border-black">
            CONTENT_MANAGEMENT_SYSTEM
          </div>
        </div>
        <button
          onClick={() => { setEditingBlog(null); setShowModal(true); }}
          className="flex items-center gap-2 bg-black text-white px-6 py-3 font-black border-4 border-yellow-400"
        >
          <Plus size={20} />
          CREATE POST
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {blogs.map((blog) => (
          <div key={blog._id} className="bg-white border-4 border-black p-6">
            <div className="mb-4">
              <div className="text-xs font-bold uppercase text-gray-600">{blog.category}</div>
              <h3 className="font-black text-2xl mb-2">{blog.title}</h3>
              <p className="text-gray-600 font-bold">{blog.excerpt}</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => { setEditingBlog(blog); setShowModal(true); }}
                className="flex-1 bg-blue-500 text-white p-2 border-2 border-black font-bold">
                <Edit size={16} className="mx-auto" />
              </button>
              <button onClick={() => handleDelete(blog._id)}
                className="flex-1 bg-red-500 text-white p-2 border-2 border-black font-bold">
                <Trash2 size={16} className="mx-auto" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <BlogModal
          blog={editingBlog}
          onClose={() => { setShowModal(false); setEditingBlog(null); }}
          onSuccess={() => { fetchBlogs(); setShowModal(false); setEditingBlog(null); }}
        />
      )}
    </div>
  );
};

const BlogModal = ({ blog, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: blog?.title || '',
    excerpt: blog?.excerpt || '',
    content: blog?.content || '',
    category: blog?.category || 'Process',
    isPublished: blog?.isPublished || false,
    isFeatured: blog?.isFeatured || false
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (blog) {
        await api.put(`/blogs/admin/${blog._id}`, formData);
      } else {
        await api.post('/blogs/admin/create', formData);
      }
      alert('Blog saved!');
      onSuccess();
    } catch (error) {
      alert('Error saving blog');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-white border-4 border-yellow-400 w-full max-w-3xl">
        <div className="bg-yellow-400 p-4 border-b-4 border-black flex justify-between">
          <h2 className="text-2xl font-black">{blog ? 'EDIT' : 'CREATE'} POST</h2>
          <button onClick={onClose} className="text-2xl font-black">×</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          <div>
            <label className="block text-xs font-black uppercase mb-2">Title*</label>
            <input required type="text" value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              className="w-full px-4 py-3 border-2 border-black font-bold" />
          </div>
          <div>
            <label className="block text-xs font-black uppercase mb-2">Category*</label>
            <select required value={formData.category}
              onChange={(e) => setFormData({...formData, category: e.target.value})}
              className="w-full px-4 py-3 border-2 border-black font-bold bg-white">
              <option value="Process">Process</option>
              <option value="Lore">Lore</option>
              <option value="Design">Design</option>
              <option value="Updates">Updates</option>
              <option value="News">News</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-black uppercase mb-2">Excerpt*</label>
            <textarea required rows="2" value={formData.excerpt}
              onChange={(e) => setFormData({...formData, excerpt: e.target.value})}
              className="w-full px-4 py-3 border-2 border-black font-mono text-sm" />
          </div>
          <div>
            <label className="block text-xs font-black uppercase mb-2">Content*</label>
            <textarea required rows="10" value={formData.content}
              onChange={(e) => setFormData({...formData, content: e.target.value})}
              className="w-full px-4 py-3 border-2 border-black font-mono text-sm" />
          </div>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 font-bold">
              <input type="checkbox" checked={formData.isPublished}
                onChange={(e) => setFormData({...formData, isPublished: e.target.checked})}
                className="w-5 h-5" />
              Published
            </label>
            <label className="flex items-center gap-2 font-bold">
              <input type="checkbox" checked={formData.isFeatured}
                onChange={(e) => setFormData({...formData, isFeatured: e.target.checked})}
                className="w-5 h-5" />
              Featured
            </label>
          </div>
          <button type="submit"
            className="w-full bg-yellow-400 text-black font-black text-lg uppercase py-4 border-4 border-black">
            {blog ? 'UPDATE POST' : 'CREATE POST'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default BlogManagement;
